/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Content Helper utilities for MathJax and Markdown processing
 *
 * @file contentHelper.js
 * @since 1.0.0
 */

export default class ContentHelper {
  /**
   * Convert content with MathJax expressions
   * @param {string} content - Content string that may contain math expressions
   * @returns {Promise<string>} - Promise that resolves with typeset HTML
   */
  static mathjaxConversion = (content) => new Promise((resolve, reject) => {
    if (
      (content.indexOf('`') >= 0 &&
          content.lastIndexOf('`') > 0 &&
          content.indexOf('`') !== content.lastIndexOf('`')) ||
        content.indexOf('\\') >= 0 ||
        content.includes('</math>')
    ) {
      // Create a temporary container element
      const tempContainer = document.createElement('span');
      // Set the content (string with math expressions) as innerHTML
      tempContainer.innerHTML = content;
      // Use MathJax.typesetPromise() to typeset only this container
      if (window.MathJax && window.MathJax.typesetPromise) {
        window.MathJax.typesetPromise([tempContainer])
          .then(() => {
            resolve(tempContainer.innerHTML); // Resolve the promise with the container's typeset HTML
            // Remove the temporary container after typesetting is done
            tempContainer.remove();
          })
          .catch((err) => {
            reject(err); // Reject the promise if an error occurs
          });
      } else {
        reject(new Error('MathJax is not loaded'));
      }
    } else {
      resolve(content);
    }
  });

  /**
   * Convert content to Markdown string
   * @param {string} content - Content string to convert
   * @returns {Promise<string>} - Promise that resolves with parsed markdown HTML
   */
  static convertToMarkedDownString = async (content) => {
    try {
      const checkCharExist = typeof content === 'string' && content && content.trim();
      if (checkCharExist && checkCharExist.length > 0) {
        if (window.marked && window.marked.parse) {
          return window.marked.parse(content);
        }
        throw new Error('Marked is not loaded');
      }

      return content;
    } catch (e) {
      console.log('Error converting to Markdown:', e);

      return content;
    }
  };
}
